import type {
    ResourceId,
    ResourceTypeHandle,
    RoleName,
    StepPath,
    WorkflowTraceHandle,
    TimestampedResource,
} from '@toolproof-core/genesis/_contract/_index';
import type { TimestampedResourcesByType } from '@toolproof-core/lib/_contract/resources';
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where, type DocumentData, type QuerySnapshot } from 'firebase/firestore';
import { db } from '@/integrations/gcp/firebaseWebInit';
import { readCosmosDiagnostics } from '@/diagnosticsState';

type ValueCacheEntry = {
    timestamp?: string;
    data: unknown;
};

// Module-level state to reduce bucket re-fetches across tab/route switches.
const valueCache = new Map<string, ValueCacheEntry>();

// Global state for items and loading status to persist across hook unmounts/re-mounts
const globalItemsByType: Record<string, TimestampedResource[]> = {};
const globalLoadingByType: Record<string, boolean> = {};
const globalSubscriptions: Record<string, { unsub: () => void; count: number }> = {};
// Store listeners as functions that take the latest data
const globalListeners: Record<string, Set<(items: TimestampedResource[], loading: boolean, error?: Error) => void>> = {};

function makeCacheKey(path: string, timestamp?: string): string {
    return `${path}@@${timestamp ?? ''}`;
}

async function fetchValue(path: string, timestamp?: string): Promise<unknown> {
    const cacheKey = makeCacheKey(path, timestamp);
    const cached = valueCache.get(cacheKey);
    if (cached) return cached.data;

    const url = `/api/fetch-projection?path=${encodeURIComponent(path)}${timestamp ? `&timestamp=${encodeURIComponent(timestamp)}` : ''}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
        throw new Error(`Failed to fetch resource data for ${path} via ${url}: ${res.status} ${res.statusText}`);
    }

    const contentType = res.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
        ? ((await res.json()) as unknown)
        : ((await res.text()) as unknown);

    valueCache.set(cacheKey, { timestamp, data });
    return data;
}

async function mapWithConcurrency<TInput, TOutput>(
    items: TInput[],
    concurrency: number,
    mapper: (item: TInput) => Promise<TOutput>
): Promise<TOutput[]> {
    const limit = Math.max(1, Math.floor(concurrency));
    const results: TOutput[] = new Array(items.length);
    let nextIndex = 0;

    async function worker(): Promise<void> {
        while (true) {
            const index = nextIndex;
            nextIndex += 1;
            if (index >= items.length) return;
            results[index] = await mapper(items[index]);
        }
    }

    const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
    await Promise.all(workers);
    return results;
}

function requireString(value: unknown, fieldName: string): string {
    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`Invalid or missing required field: ${fieldName}`);
    }
    return value;
}

function requirePrefixedString(value: unknown, fieldName: string, prefix: string): string {
    const s = requireString(value, fieldName);
    if (!s.startsWith(prefix)) {
        throw new Error(`Invalid ${fieldName} (expected ${prefix}*): ${s}`);
    }
    return s;
}

function requireResourceId(value: unknown, fieldName: string): ResourceId {
    return requirePrefixedString(value, fieldName, 'RESOURCE-') as ResourceId;
}

function requireResourceTypeHandle(value: unknown, fieldName: string): ResourceTypeHandle {
    return requirePrefixedString(value, fieldName, 'TYPE-') as ResourceTypeHandle;
}

function requireRoleName(value: unknown, fieldName: string): RoleName {
    return requirePrefixedString(value, fieldName, 'ROLE-') as RoleName;
}

function requireStepPath(value: unknown, fieldName: string): StepPath {
    return requireString(value, fieldName) as StepPath;
}


function requireWorkflowTraceHandle(value: unknown, fieldName: string): WorkflowTraceHandle {
    return requirePrefixedString(value, fieldName, 'WORKFLOW_TRACE-') as WorkflowTraceHandle;
}

type ParsedStoredResource = {
    resource: TimestampedResource;
    path: string;
    timestamp: string;
};

function parseMaterializedRecordFromDoc(resourceTypeHandleFromPath: ResourceTypeHandle, docId: string, raw: Record<string, unknown>): ParsedStoredResource {
    const id = requireResourceId(
        (typeof raw.id === 'string' && raw.id.length > 0) ? raw.id : docId,
        'id'
    );

    const resourceTypeHandleField = raw.resourceTypeHandle === undefined
        ? resourceTypeHandleFromPath
        : requireResourceTypeHandle(raw.resourceTypeHandle, 'resourceTypeHandle');


    const provenance = raw.provenance;
    if (!provenance || typeof provenance !== 'object') {
        throw new Error('Invalid or missing required field: provenance');
    }

    const provenanceRecord = provenance as Record<string, unknown>;
    const provenanceKind = provenanceRecord.provenanceKind;

    const timestamp = requireString(raw.timestamp, 'timestamp');
    const path = `${resourceTypeHandleField}/${id}`;

    if (provenanceKind === 'genesis') {
        return {
            resource: {
                id,
                resourceTypeHandle: resourceTypeHandleField,
                provenance: {
                    provenanceKind: 'genesis',
                },
                timestamp,
                projection: null,
            },
            timestamp,
            path,
        };
    }

    if (provenanceKind === 'runtime') {
        return {
            resource: {
                id,
                resourceTypeHandle: resourceTypeHandleField,
                provenance: {
                    provenanceKind: 'runtime',
                },
                timestamp,
                projection: null,
            },
            timestamp,
            path,
        };
    }

    if (provenanceKind !== 'workflow') {
        throw new Error(`Invalid provenance.provenanceKind: ${String(provenanceKind)}`);
    }

    const toolStepRoleAddress = provenanceRecord.toolStepRoleAddress;
    if (!toolStepRoleAddress || typeof toolStepRoleAddress !== 'object') {
        throw new Error('Invalid or missing required field: provenance.toolStepRoleAddress');
    }

    const toolStepRoleAddressRecord = toolStepRoleAddress as Record<string, unknown>;

    const roleName = requireRoleName(toolStepRoleAddressRecord.roleName, 'provenance.toolStepRoleAddress.roleName');

    const stepPath = requireStepPath(toolStepRoleAddressRecord.stepPath, 'provenance.toolStepRoleAddress.stepPath');

    const workflowTraceHandle = requireWorkflowTraceHandle(
        provenanceRecord.workflowTraceHandle,
        'provenance.workflowTraceHandle'
    );

    return {
        resource: {
            id,
            resourceTypeHandle: resourceTypeHandleField,
            provenance: {
                provenanceKind: 'workflow',
                workflowTraceHandle,
                toolStepRoleAddress: {
                    roleName,
                    stepPath,
                },
            },
            timestamp,
            projection: null,
        },
        timestamp,
        path,
    };
}

function notifyListeners(resourceTypeHandle: string, items: TimestampedResource[], loading: boolean, error?: Error) {
    const listeners = globalListeners[resourceTypeHandle];
    if (listeners) {
        listeners.forEach((l) => l(items, loading, error));
    }
}

export function useResources(
    resourceTypeHandles: ResourceTypeHandle[],
    options?: { minTimestamp?: string }
): { items: TimestampedResourcesByType; loading: boolean; error: Error | undefined } {
    const minTimestamp = options?.minTimestamp;
    const useGlobalPooling = !minTimestamp;
    const resourceTypeHandlesKey = useMemo(
        () => [...resourceTypeHandles].sort().join('|'),
        [resourceTypeHandles]
    );

    const resourceTypeHandleList = useMemo(
        () => (resourceTypeHandlesKey.length > 0
            ? (resourceTypeHandlesKey.split('|') as ResourceTypeHandle[])
            : []),
        [resourceTypeHandlesKey]
    );

    // Initialize from global state to avoid flashing "loading" state
    const [itemsByType, setItemsByType] = useState<TimestampedResourcesByType>(() => {
        const initial: TimestampedResourcesByType = {};
        if (!useGlobalPooling) return initial;
        resourceTypeHandles.forEach((resourceTypeHandle) => {
            if (globalItemsByType[resourceTypeHandle]) initial[resourceTypeHandle] = globalItemsByType[resourceTypeHandle];
        });
        return initial;
    });

    const [loadingByType, setLoadingByType] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {};
        resourceTypeHandles.forEach((resourceTypeHandle) => {
            initial[resourceTypeHandle] = useGlobalPooling
                ? (globalLoadingByType[resourceTypeHandle] ?? true)
                : true;
        });
        return initial;
    });

    const [error, setError] = useState<Error | undefined>(undefined);

    useEffect(() => {
        let mounted = true;

        const scheduleState = (fn: () => void) => {
            if (typeof queueMicrotask === 'function') queueMicrotask(fn);
            else void Promise.resolve().then(fn);
        };

        if (resourceTypeHandleList.length === 0) {
            scheduleState(() => {
                if (!mounted) return;
                setItemsByType({});
                setLoadingByType({});
                setError(undefined);
            });
            return () => {
                mounted = false;
            };
        }

        // Helper to update local state (and global cache when pooling is enabled)
        const updateState = (resourceTypeHandle: ResourceTypeHandle, items: TimestampedResource[], isLoading: boolean, err?: Error) => {
            if (useGlobalPooling) {
                globalItemsByType[resourceTypeHandle] = items;
                globalLoadingByType[resourceTypeHandle] = isLoading;
            }
            if (mounted) {
                setItemsByType((prev) => ({ ...prev, [resourceTypeHandle]: items }));
                setLoadingByType((prev) => ({ ...prev, [resourceTypeHandle]: isLoading }));
                if (err) setError(err);
            }
            if (useGlobalPooling) {
                notifyListeners(resourceTypeHandle, items, isLoading, err);
            }
        };

        const makeSnapshotHandler = (resourceTypeHandle: ResourceTypeHandle, snapshotSeqByType: { seq: number }) => (snap: QuerySnapshot<DocumentData>) => {
            if (readCosmosDiagnostics().resources) {
                console.log('[useResources] snapshot', {
                    resourceTypeHandle,
                    docCount: snap.size,
                    docIds: snap.docs.slice(0, 5).map((d) => d.id),
                });
            }

            const currentSeq = ++snapshotSeqByType.seq;
            const docs = snap.docs;
            const parseErrors: string[] = [];
            const baseResources: Array<ParsedStoredResource & { needsHydration: boolean }> = [];

            for (const d of docs) {
                try {
                    const raw = d.data() as Record<string, unknown>;
                    const record = parseMaterializedRecordFromDoc(resourceTypeHandle, d.id, raw);
                    baseResources.push({
                        ...record,
                        needsHydration: true,
                    });
                } catch (e) {
                    const message = e instanceof Error ? e.message : String(e);
                    parseErrors.push(`${resourceTypeHandle}/${d.id}: ${message}`);
                }
            }

            const currentItems = baseResources.map((x) => x.resource);
            let currentErr: Error | undefined = undefined;
            if (parseErrors.length) {
                currentErr = new Error(`Some resource docs could not be parsed:\n${parseErrors.slice(0, 10).join('\n')}${parseErrors.length > 10 ? `\n...and ${parseErrors.length - 10} more` : ''}`);
            }

            updateState(resourceTypeHandle, currentItems, false, currentErr);

            // Hydrate value
            const toHydrate = baseResources
                .filter((x) => x.needsHydration && x.path.length > 0)
                .map((x) => ({ id: x.resource.id, path: x.path, timestamp: x.timestamp }));

            if (toHydrate.length > 0) {
                void (async () => {
                    try {
                        const hydrated = await mapWithConcurrency(toHydrate, 8, async (item) => {
                            const data = await fetchValue(item.path, item.timestamp);
                            return { id: item.id, projection: data };
                        });

                        if (!mounted) return;
                        if (snapshotSeqByType.seq !== currentSeq) return;

                        if (useGlobalPooling) {
                            const existing = globalItemsByType[resourceTypeHandle] ?? [];
                            if (!existing.length) return;
                            const byId = new Map(existing.map((r) => [r.id, r]));
                            for (const h of hydrated) {
                                const prior = byId.get(h.id);
                                if (!prior) continue;
                                byId.set(h.id, { ...prior, projection: h.projection });
                            }
                            updateState(resourceTypeHandle, Array.from(byId.values()), false);
                            return;
                        }

                        setItemsByType((prev) => {
                            const existing = (prev as Partial<TimestampedResourcesByType>)[resourceTypeHandle] ?? currentItems;
                            if (!existing.length) return prev;
                            const byId = new Map(existing.map((r) => [r.id, r]));
                            for (const h of hydrated) {
                                const prior = byId.get(h.id);
                                if (!prior) continue;
                                byId.set(h.id, { ...prior, projection: h.projection, timestamp: prior.timestamp });
                            }
                            return { ...prev, [resourceTypeHandle]: Array.from(byId.values()) };
                        });
                    } catch (e) {
                        console.error('[useResources] value hydration failed', e);
                        if (useGlobalPooling) {
                            updateState(resourceTypeHandle, globalItemsByType[resourceTypeHandle] || [], false, e as Error);
                        } else if (mounted) {
                            setError(e as Error);
                        }
                    }
                })();
            }
        };

        const cleanups = resourceTypeHandleList.map((resourceTypeHandle) => {
            const snapshotSeqByType = { seq: 0 };
            const membersCollection = collection(db, 'resources', resourceTypeHandle, 'members');
            const membersQuery = minTimestamp
                ? query(membersCollection, where('timestamp', '>=', minTimestamp))
                : membersCollection;

            if (readCosmosDiagnostics().resources) {
                console.log('[useResources] subscribe', {
                    resourceTypeHandle,
                    minTimestamp: minTimestamp ?? null,
                    useGlobalPooling,
                });
            }

            if (!useGlobalPooling) {
                scheduleState(() => {
                    if (!mounted) return;
                    setLoadingByType((prev) => ({ ...prev, [resourceTypeHandle]: true }));
                });

                const unsub = onSnapshot(
                    membersQuery,
                    makeSnapshotHandler(resourceTypeHandle, snapshotSeqByType),
                    (err) => {
                        console.error('[useResources] snapshot error', {
                            resourceTypeHandle,
                            message: err?.message ?? String(err),
                        });
                        if (!mounted) return;
                        setLoadingByType((prev) => ({ ...prev, [resourceTypeHandle]: false }));
                        setError(err);
                    }
                );

                return () => {
                    unsub();
                };
            }

            // Global pooling path
            if (!globalListeners[resourceTypeHandle]) {
                globalListeners[resourceTypeHandle] = new Set();
            }

            const listener = (items: TimestampedResource[], isLoading: boolean, err?: Error) => {
                if (!mounted) return;
                setItemsByType((prev) => ({ ...prev, [resourceTypeHandle]: items }));
                setLoadingByType((prev) => ({ ...prev, [resourceTypeHandle]: isLoading }));
                if (err) setError(err);
            };

            globalListeners[resourceTypeHandle].add(listener);

            if (!globalSubscriptions[resourceTypeHandle]) {
                if (readCosmosDiagnostics().resources) console.debug('[useResources] creating new subscription for', resourceTypeHandle);
                if (readCosmosDiagnostics().resources) {
                    console.log('[useResources] subscription mode', {
                        resourceTypeHandle,
                        mode: 'create',
                    });
                }

                globalLoadingByType[resourceTypeHandle] = true;
                scheduleState(() => {
                    if (!mounted) return;
                    setLoadingByType((prev) => ({ ...prev, [resourceTypeHandle]: true }));
                });

                const unsub = onSnapshot(
                    membersQuery,
                    makeSnapshotHandler(resourceTypeHandle, snapshotSeqByType),
                    (err) => {
                        console.error('[useResources] snapshot error', {
                            resourceTypeHandle,
                            message: err?.message ?? String(err),
                        });
                        updateState(resourceTypeHandle, globalItemsByType[resourceTypeHandle] || [], false, err);
                    }
                );

                globalSubscriptions[resourceTypeHandle] = { unsub, count: 1 };
            } else {
                if (readCosmosDiagnostics().resources) {
                    console.log('[useResources] subscription mode', {
                        resourceTypeHandle,
                        mode: 'reuse',
                        countBefore: globalSubscriptions[resourceTypeHandle].count,
                    });
                }
                globalSubscriptions[resourceTypeHandle].count++;
                if (globalItemsByType[resourceTypeHandle]) {
                    scheduleState(() => {
                        if (!mounted) return;
                        setItemsByType((prev) => ({ ...prev, [resourceTypeHandle]: globalItemsByType[resourceTypeHandle] }));
                        setLoadingByType((prev) => ({ ...prev, [resourceTypeHandle]: false }));
                    });
                }
            }

            return () => {
                globalListeners[resourceTypeHandle].delete(listener);
                globalSubscriptions[resourceTypeHandle].count--;
                if (readCosmosDiagnostics().resources) {
                    console.log('[useResources] subscription cleanup', {
                        resourceTypeHandle,
                        countAfter: globalSubscriptions[resourceTypeHandle].count,
                    });
                }
                if (globalSubscriptions[resourceTypeHandle].count === 0) {
                    if (readCosmosDiagnostics().resources) console.debug('[useResources] closing subscription for', resourceTypeHandle);
                    globalSubscriptions[resourceTypeHandle].unsub();
                    delete globalSubscriptions[resourceTypeHandle];
                }
            };
        });

        return () => {
            mounted = false;
            cleanups.forEach((c) => c());
        };
    }, [resourceTypeHandleList, resourceTypeHandlesKey, minTimestamp, useGlobalPooling]);

    const loading = resourceTypeHandleList.length === 0
        ? false
        : resourceTypeHandleList.some((resourceTypeHandle) => loadingByType[resourceTypeHandle] ?? true);

    return { items: itemsByType, loading, error };
}